import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Check, Clock, FileText, Users, Send, AlertTriangle, Eye } from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = ['CODE_OF_CONDUCT', 'PRIVACY', 'INCIDENT', 'MEDICATION', 'SAFETY', 'OTHER'];

export default function StaffPoliciesPage() {
  const [activeTab, setActiveTab] = useState('my-pending');
  const [myPending, setMyPending] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [showAssign, setShowAssign] = useState(false);
  const [stats, setStats] = useState(null);

  const fetchMyPending = async () => {
    try {
      const res = await api.get('/policies/my/pending');
      setMyPending(res.data.items || []);
    } catch (err) {
      // User might not have staff profile - that's ok
      setMyPending([]);
    }
  };

  const fetchAllPolicies = async () => {
    setLoading(true);
    try {
      const res = await api.get('/policies?pageSize=100');
      setAllPolicies(res.data.items || []);
    } catch (err) {
      toast.error('Failed to load policies');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await api.get('/policies/stats/summary');
      setStats(res.data.data);
    } catch (err) {}
  };

  useEffect(() => {
    fetchMyPending();
    fetchAllPolicies();
    fetchStats();
  }, []);

  const handleAcknowledge = async (policyId) => {
    try {
      await api.post(`/policies/${policyId}/acknowledge`);
      toast.success('Policy acknowledged successfully');
      fetchMyPending();
      fetchAllPolicies();
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to acknowledge policy');
    }
  };

  return (
    <div className="space-y-6" data-testid="staff-policies-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Staff Policies</h1>
          <p className="text-muted-foreground">View and acknowledge organizational policies</p>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Total Policies" value={stats.totalPolicies} icon={FileText} />
          <StatCard label="Total Assigned" value={stats.acknowledgements?.totalAssigned} icon={Users} />
          <StatCard label="Acknowledged" value={stats.acknowledgements?.totalAcknowledged} icon={Check} color="text-green-600" />
          <StatCard label="Pending" value={stats.acknowledgements?.totalPending} icon={Clock} color="text-orange-600" />
        </div>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="my-pending">
            My Pending ({myPending.length})
          </TabsTrigger>
          <TabsTrigger value="all-policies">All Policies</TabsTrigger>
        </TabsList>

        <TabsContent value="my-pending" className="mt-4">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Assigned Date</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {myPending.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                        <Check className="h-8 w-8 mx-auto mb-2 text-green-600" />
                        No pending policy acknowledgements
                      </TableCell>
                    </TableRow>
                  ) : myPending.map(item => (
                    <TableRow key={item.id} className="hover:bg-muted/50" data-testid={`pending-row-${item.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium">{item.policy?.title || 'Policy'}</p>
                            <p className="text-xs text-muted-foreground">{item.policy?.policyNo}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell><StatusBadge status={item.policy?.category} /></TableCell>
                      <TableCell>v{item.policyVersion}</TableCell>
                      <TableCell className="text-sm">{item.assignedAt?.slice(0, 10)}</TableCell>
                      <TableCell><StatusBadge status={item.status} /></TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          {item.documentPath && (
                            <Button size="sm" variant="outline" onClick={() => window.open(item.documentPath, '_blank')}>
                              <Eye className="h-4 w-4 mr-1" />View
                            </Button>
                          )}
                          <Button size="sm" onClick={() => handleAcknowledge(item.policyId)} data-testid={`acknowledge-${item.id}`}>
                            <Check className="h-4 w-4 mr-1" />Acknowledge
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="all-policies" className="mt-4">
          <Card>
            <CardHeader className="pb-3 flex flex-row items-center justify-between">
              <CardTitle className="text-lg">All Policies</CardTitle>
              <Dialog open={showAssign} onOpenChange={setShowAssign}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="assign-policy-btn"><Send className="h-4 w-4 mr-2" />Assign Policy</Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader><DialogTitle>Assign Policy to Staff</DialogTitle></DialogHeader>
                  <AssignPolicyForm policies={allPolicies} onSuccess={() => { setShowAssign(false); fetchAllPolicies(); fetchStats(); }} />
                </DialogContent>
              </Dialog>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Policy No</TableHead>
                    <TableHead>Title</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Version</TableHead>
                    <TableHead>Assigned</TableHead>
                    <TableHead>Acknowledged</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8">Loading...</TableCell></TableRow>
                  ) : allPolicies.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">No policies found</TableCell></TableRow>
                  ) : allPolicies.map(policy => (
                    <TableRow key={policy.id} className="hover:bg-muted/50" data-testid={`policy-row-${policy.id}`}>
                      <TableCell className="font-mono text-sm">{policy.policyNo}</TableCell>
                      <TableCell className="font-medium">{policy.title}</TableCell>
                      <TableCell><StatusBadge status={policy.category} /></TableCell>
                      <TableCell>{policy.activeVersion ? `v${policy.activeVersion}` : '-'}</TableCell>
                      <TableCell>{policy.acknowledgementStats?.totalAssigned || 0}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <span>{policy.acknowledgementStats?.totalAcknowledged || 0}</span>
                          {policy.acknowledgementStats?.pendingCount > 0 && (
                            <AlertTriangle className="h-3.5 w-3.5 text-orange-500" />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setSelectedPolicy(policy)} data-testid={`view-acks-${policy.id}`}>
                          <Users className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!selectedPolicy} onOpenChange={() => setSelectedPolicy(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Acknowledgements - {selectedPolicy?.title}</DialogTitle></DialogHeader>
          {selectedPolicy && <AcknowledgementsList policyId={selectedPolicy.id} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color = 'text-primary' }) {
  return (
    <Card>
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`p-2 rounded-lg bg-muted ${color}`}><Icon className="h-5 w-5" /></div>
        <div>
          <p className="text-2xl font-bold">{value || 0}</p>
          <p className="text-xs text-muted-foreground">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
}

function AssignPolicyForm({ policies, onSuccess }) {
  const [policyId, setPolicyId] = useState('');
  const [staffIds, setStaffIds] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get('/staff?pageSize=100').then(r => setAllStaff(r.data.items || []));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!policyId || staffIds.length === 0) {
      toast.error('Please select a policy and at least one staff member');
      return;
    }
    setLoading(true);
    try {
      await api.post(`/policies/${policyId}/assign`, { staffIds });
      toast.success('Policy assigned successfully');
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || 'Failed to assign policy');
    } finally {
      setLoading(false);
    }
  };

  const toggleStaff = (staffId) => {
    setStaffIds(prev => prev.includes(staffId) ? prev.filter(id => id !== staffId) : [...prev, staffId]);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label>Policy *</Label>
        <Select value={policyId} onValueChange={setPolicyId}>
          <SelectTrigger><SelectValue placeholder="Select policy" /></SelectTrigger>
          <SelectContent>
            {policies.filter(p => p.activeVersion).map(p => (
              <SelectItem key={p.id} value={p.id}>{p.policyNo} - {p.title}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label>Staff Members * ({staffIds.length} selected)</Label>
        <div className="border rounded-lg max-h-[200px] overflow-y-auto mt-2">
          {allStaff.map(staff => (
            <label key={staff.id} className="flex items-center gap-2 p-2 hover:bg-muted cursor-pointer">
              <input
                type="checkbox"
                checked={staffIds.includes(staff.id)}
                onChange={() => toggleStaff(staff.id)}
                className="rounded"
              />
              <span className="text-sm">{staff.user_full_name || staff.full_name || 'Staff'}</span>
            </label>
          ))}
        </div>
      </div>
      <Button type="submit" disabled={loading} className="w-full">{loading ? 'Assigning...' : 'Assign Policy'}</Button>
    </form>
  );
}

function AcknowledgementsList({ policyId }) {
  const [acks, setAcks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/policies/${policyId}/acknowledgements?pageSize=100`)
      .then(r => setAcks(r.data.items || []))
      .catch(() => toast.error('Failed to load acknowledgements'))
      .finally(() => setLoading(false));
  }, [policyId]);

  if (loading) return <div className="py-8 text-center">Loading...</div>;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Staff Member</TableHead>
          <TableHead>Version</TableHead>
          <TableHead>Status</TableHead>
          <TableHead>Assigned</TableHead>
          <TableHead>Acknowledged</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {acks.length === 0 ? (
          <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">No acknowledgements</TableCell></TableRow>
        ) : acks.map(ack => (
          <TableRow key={ack.id}>
            <TableCell>
              <div>
                <p className="font-medium">{ack.staffName || 'Staff'}</p>
                <p className="text-xs text-muted-foreground">{ack.staffEmail}</p>
              </div>
            </TableCell>
            <TableCell>v{ack.policyVersion}</TableCell>
            <TableCell><StatusBadge status={ack.status} /></TableCell>
            <TableCell className="text-sm">{ack.assignedAt?.slice(0, 10)}</TableCell>
            <TableCell className="text-sm">{ack.acknowledgedAt?.slice(0, 10) || '-'}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
